import React, { useState, useEffect } from 'react';

const WARDS = Array.from({ length: 14 }, (_, i) => `Ward ${i + 1}`);
const STATUSES = ['draft', 'active', 'threshold_reached', 'sent', 'archived'];
const STATUS_COLORS = {
  draft: 'text-gray-500 border-gray-700',
  active: 'text-green-400 border-green-800',
  threshold_reached: 'text-red-400 border-red-800 animate-pulse',
  sent: 'text-blue-400 border-blue-800',
  archived: 'text-gray-700 border-gray-800',
};

const EMPTY_FORM = {
  ward: 'Ward 1', title: '', description: '', councillor_name: '',
  threshold: 25, cooldown_days: 14, sort_order: 0, source_meeting_id: '',
};

// ── V2 User Questions Panel ───────────────────────────────────────────────────
const V2QuestionsPanel = ({ api, toast }) => {
  const [questions, setQuestions] = useState([]);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(false);

  const currentMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const fetchQuestions = async (m) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/ward-pulse-v2/questions?month_year=${m || currentMonth()}`);
      setQuestions(res.data?.questions || []);
      setMonth(res.data?.month_year || m || currentMonth());
    } catch { toast?.error?.('Failed to load V2 questions'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQuestions(currentMonth()); }, []);

  const sendQuestion = async (id) => {
    try {
      await api.post(`/admin/ward-pulse-v2/questions/${id}/send`);
      toast?.success?.('Marked as sent to councillor');
      fetchQuestions(month);
    } catch { toast?.error?.('Failed to mark sent'); }
  };

  const removeQuestion = async (id, questionText) => {
    if (!window.confirm(`Remove this question?\n\n"${questionText.slice(0, 100)}"`)) return;
    try {
      await api.delete(`/admin/ward-pulse-v2/questions/${id}`);
      toast?.success?.('Question removed and hidden from users');
      fetchQuestions(month);
    } catch { toast?.error?.('Failed to remove'); }
  };

  const live = questions.filter(q => q.status === 'approved');
  const sent = questions.filter(q => q.status === 'sent');
  const rejected = questions.filter(q => q.status === 'rejected');

  const QuestionRow = ({ q, showSend = true }) => (
    <div key={q.id} className="bg-[#111] border border-[#2a2a2a] rounded p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono text-[10px] text-gray-500">{q.ward}</span>
            <span className="font-mono text-[10px] text-gray-600">@{q.username}</span>
            <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${
              q.vote_count > 0 ? 'bg-green-900/30 text-green-400' : 'bg-[#1a1a1a] text-gray-600'
            }`}>
              {q.vote_count} vote{q.vote_count !== 1 ? 's' : ''}
            </span>
            {q.status === 'sent' && (
              <span className="font-mono text-[10px] text-blue-400">SENT</span>
            )}
          </div>
          <p className="text-white text-sm leading-snug">{q.question_text}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {showSend && q.status !== 'sent' && (
            <button
              onClick={() => sendQuestion(q.id)}
              className="text-blue-400 border border-blue-800 text-xs px-3 py-1.5 hover:bg-blue-900/30 transition-colors rounded"
            >
              SEND
            </button>
          )}
          {q.status !== 'rejected' && (
            <button
              onClick={() => removeQuestion(q.id, q.question_text)}
              className="text-red-500 border border-red-900/60 text-xs px-3 py-1.5 hover:bg-red-950/40 transition-colors rounded"
            >
              REMOVE
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-[#0a0a0a] border border-[#C5A52A]/30 rounded p-5 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-[#C5A52A] font-bold text-sm uppercase tracking-wider">User-Submitted Questions</h3>
          <p className="text-gray-600 text-xs mt-0.5">Top 2 point earners per ward submit · ward votes · you send to councillor</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => fetchQuestions(month)} className="text-gray-500 text-xs hover:text-white transition-colors">↻ Refresh</button>
          <input
            type="month"
            value={month}
            onChange={e => fetchQuestions(e.target.value)}
            className="bg-[#111] border border-[#2a2a2a] text-white text-xs px-2 py-1 rounded"
          />
        </div>
      </div>

      {loading && <p className="text-gray-600 text-xs">Loading…</p>}

      {!loading && live.length === 0 && sent.length === 0 && (
        <p className="text-gray-700 text-xs">No user-submitted questions for {month}</p>
      )}

      {/* LIVE — currently visible to ward users, can be removed at any time */}
      {live.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <p className="text-xs text-green-400 uppercase tracking-widest font-mono">Live — visible to users ({live.length})</p>
          </div>
          <div className="space-y-2">
            {live.map(q => <QuestionRow key={q.id} q={q} showSend={true} />)}
          </div>
        </div>
      )}

      {/* SENT */}
      {sent.length > 0 && (
        <div>
          <p className="text-xs text-blue-400 uppercase tracking-widest font-mono mb-2">Sent to councillor ({sent.length})</p>
          <div className="space-y-2">
            {sent.map(q => <QuestionRow key={q.id} q={q} showSend={false} />)}
          </div>
        </div>
      )}

      {/* REMOVED */}
      {rejected.length > 0 && (
        <details className="cursor-pointer">
          <summary className="text-xs text-gray-700 uppercase tracking-widest font-mono">Removed ({rejected.length})</summary>
          <div className="space-y-1 mt-2">
            {rejected.map(q => (
              <div key={q.id} className="flex items-center gap-3 py-1.5 border-t border-[#1a1a1a] opacity-50">
                <span className="text-[10px] text-red-800 font-mono">REMOVED</span>
                <span className="text-xs text-gray-600 flex-1">{q.ward} · {q.question_text.slice(0, 80)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const WardPulseAdmin = ({ api, toast }) => {
  const [issues, setIssues] = useState([]);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [responseModal, setResponseModal] = useState(null); // issue object
  const [responseText, setResponseText] = useState('');
  const [editing, setEditing] = useState(null); // issue id being edited inline

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set('ward', filter);
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/admin/ward-pulse?${params.toString()}`);
      setIssues(res.data?.issues || []);
    } catch { toast?.error?.('Failed to load Ward Pulse issues'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchIssues(); }, [filter, statusFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/ward-pulse', { ...form, threshold: Number(form.threshold), sort_order: Number(form.sort_order) });
      toast?.success?.('Issue created');
      setShowCreate(false);
      setForm(EMPTY_FORM);
      fetchIssues();
    } catch (err) { toast?.error?.(err?.response?.data?.detail || 'Failed to create'); }
  };

  const action = async (url, method = 'post', body = {}) => {
    try {
      await api[method](url, body);
      fetchIssues();
    } catch (err) { toast?.error?.(err?.response?.data?.detail || 'Action failed'); }
  };

  const sendResponse = async () => {
    if (!responseText.trim()) return;
    try {
      await api.post(`/admin/ward-pulse/${responseModal.id}/councillor-response`, { response_text: responseText });
      toast?.success?.('Response posted and pinned to ward feed for 48h');
      setResponseModal(null);
      setResponseText('');
      fetchIssues();
    } catch (err) { toast?.error?.(err?.response?.data?.detail || 'Failed to post response'); }
  };

  const thresholdReached = issues.filter(i => i.status === 'threshold_reached');
  const activeIssues    = issues.filter(i => i.status === 'active');

  return (
    <div className="space-y-6 font-mono">
      {/* V2: User-submitted questions */}
      <V2QuestionsPanel api={api} toast={toast} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white tracking-wider">WARD PULSE</h2>
          <p className="text-gray-600 text-xs mt-1">Curate ward issues · Monitor asks · Send collective correspondence</p>
        </div>
        <button
          onClick={() => setShowCreate(v => !v)}
          className="bg-red-700 hover:bg-red-600 text-white text-xs px-4 py-2 border border-red-600 transition-colors"
          data-testid="ward-pulse-create-btn"
        >
          + NEW ISSUE
        </button>
      </div>

      {/* Threshold alert */}
      {thresholdReached.length > 0 && (
        <div className="border border-red-600 bg-red-950/20 p-4">
          <p className="text-red-400 text-xs uppercase tracking-widest mb-2">
            {thresholdReached.length} issue{thresholdReached.length > 1 ? 's' : ''} reached threshold — ready for review
          </p>
          {thresholdReached.map(i => (
            <div key={i.id} className="flex items-center justify-between py-2 border-t border-red-900/30">
              <div>
                <p className="text-white text-sm">{i.title}</p>
                <p className="text-gray-500 text-xs">{i.ward} · {i.ask_count} asks</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => action(`/admin/ward-pulse/${i.id}/mark-sent`)}
                  className="text-blue-400 border border-blue-800 text-xs px-3 py-1 hover:bg-blue-900/30"
                  data-testid={`ward-pulse-mark-sent-${i.id}`}
                >
                  MARK SENT
                </button>
                <button
                  onClick={() => { setResponseModal(i); setResponseText(''); }}
                  className="text-green-400 border border-green-800 text-xs px-3 py-1 hover:bg-green-900/30"
                  data-testid={`ward-pulse-post-response-${i.id}`}
                >
                  POST RESPONSE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {STATUSES.map(s => {
          const count = issues.filter(i => i.status === s).length;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
              className={`bg-[#0d0d0d] border p-3 text-left transition-colors hover:border-gray-600 ${statusFilter === s ? 'border-white' : 'border-[#1a1a1a]'}`}
            >
              <p className="text-xs text-gray-600 uppercase tracking-wider">{s.replace('_', ' ')}</p>
              <p className={`text-2xl font-black mt-1 ${STATUS_COLORS[s]?.split(' ')[0] || 'text-white'}`}>{count}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="bg-[#111] border border-[#2a2a2a] text-white text-xs px-3 py-2 focus:outline-none focus:border-red-600"
        >
          <option value="">All Wards</option>
          {WARDS.map(w => <option key={w} value={w}>{w}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-[#111] border border-[#2a2a2a] text-white text-xs px-3 py-2 focus:outline-none focus:border-red-600"
        >
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={fetchIssues} className="text-gray-500 hover:text-white text-xs border border-[#2a2a2a] px-3 py-2">REFRESH</button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-[#0d0d0d] border border-[#2a2a2a] p-5 space-y-4">
          <h3 className="text-sm text-white tracking-widest">NEW WARD PULSE ISSUE</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Ward *</label>
              <select value={form.ward} onChange={e => setForm(p => ({ ...p, ward: e.target.value }))}
                className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 focus:outline-none focus:border-red-600">
                {WARDS.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Councillor Name</label>
              <input value={form.councillor_name} onChange={e => setForm(p => ({ ...p, councillor_name: e.target.value }))}
                placeholder="e.g. Coun. Sharron Bryce" className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 focus:outline-none focus:border-red-600" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500 uppercase block mb-1">Issue / Question *</label>
              <input required value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Why was the $2.4M park contract awarded without public tender?" className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 focus:outline-none focus:border-red-600" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500 uppercase block mb-1">Context (optional)</label>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={3} placeholder="Brief background for residents..." className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 focus:outline-none focus:border-red-600 resize-none" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Ask Threshold</label>
              <input type="number" min={1} value={form.threshold} onChange={e => setForm(p => ({ ...p, threshold: e.target.value }))}
                className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 focus:outline-none focus:border-red-600" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Sort Order (lower = first)</label>
              <input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))}
                className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 focus:outline-none focus:border-red-600" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Source Meeting ID (optional)</label>
              <input value={form.source_meeting_id} onChange={e => setForm(p => ({ ...p, source_meeting_id: e.target.value }))}
                placeholder="From Accountability Auditor" className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 focus:outline-none focus:border-red-600" />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="bg-red-700 hover:bg-red-600 text-white text-xs px-6 py-2 border border-red-600">CREATE AS DRAFT</button>
            <button type="button" onClick={() => setShowCreate(false)} className="text-gray-500 hover:text-white text-xs border border-[#2a2a2a] px-4 py-2">CANCEL</button>
          </div>
        </form>
      )}

      {/* Issues list */}
      {loading ? (
        <p className="text-gray-600 text-xs text-center py-8">Loading...</p>
      ) : issues.length === 0 ? (
        <div className="text-center py-12 border border-[#1a1a1a]">
          <p className="text-gray-600 text-sm">No issues found.</p>
          <p className="text-gray-700 text-xs mt-2">Create your first issue to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {issues.map(issue => (
            <div key={issue.id} className={`border ${issue.status === 'threshold_reached' ? 'border-red-800/60 bg-red-950/10' : 'border-[#1a1a1a] bg-[#0d0d0d]'} p-4`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${STATUS_COLORS[issue.status] || 'text-gray-500 border-gray-700'}`}>
                      {issue.status.replace('_', ' ')}
                    </span>
                    <span className="text-gray-600 text-xs">{issue.ward}</span>
                    {issue.councillor_name && <span className="text-gray-700 text-xs">· {issue.councillor_name}</span>}
                  </div>
                  <p className="text-white text-sm leading-snug">{issue.title}</p>
                  {issue.description && <p className="text-gray-600 text-xs mt-1 line-clamp-2">{issue.description}</p>}
                  {/* Progress */}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-32 h-1 bg-[#1a1a1a] rounded-full overflow-hidden">
                      <div className="h-full bg-red-700 rounded-full" style={{ width: `${Math.min(100, Math.round((issue.ask_count / (issue.threshold || 25)) * 100))}%` }} />
                    </div>
                    <span className="text-gray-600 text-xs">{issue.ask_count}/{issue.threshold || 25} asks</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  {issue.status === 'draft' && (
                    <button onClick={() => action(`/admin/ward-pulse/${issue.id}/activate`)}
                      className="text-green-400 border border-green-900 text-[9px] px-2 py-1 hover:bg-green-900/20" data-testid={`pulse-activate-${issue.id}`}>
                      ACTIVATE
                    </button>
                  )}
                  {issue.status === 'threshold_reached' && (
                    <>
                      <button onClick={() => action(`/admin/ward-pulse/${issue.id}/mark-sent`)}
                        className="text-blue-400 border border-blue-900 text-[9px] px-2 py-1 hover:bg-blue-900/20">
                        MARK SENT
                      </button>
                      <button onClick={() => { setResponseModal(issue); setResponseText(''); }}
                        className="text-green-400 border border-green-900 text-[9px] px-2 py-1 hover:bg-green-900/20">
                        POST RESPONSE
                      </button>
                    </>
                  )}
                  {!['archived', 'sent'].includes(issue.status) && (
                    <button onClick={() => action(`/admin/ward-pulse/${issue.id}/archive`)}
                      className="text-gray-600 border border-gray-800 text-[9px] px-2 py-1 hover:text-red-400 hover:border-red-900">
                      ARCHIVE
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Councillor Response Modal */}
      {responseModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d0d0d] border border-red-800 w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-red-900/30 flex items-center justify-between">
              <div>
                <p className="text-red-400 text-xs font-mono uppercase tracking-widest">Post Councillor Response</p>
                <p className="text-white text-sm mt-1">{responseModal.title}</p>
              </div>
              <button onClick={() => setResponseModal(null)} className="text-gray-600 hover:text-white text-xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-red-950/20 border border-red-900/30 px-4 py-2">
                <p className="text-red-400 text-xs font-mono">This will be posted as a public, featured post pinned to {responseModal.ward}'s feed. It is attributed to {responseModal.councillor_name || 'the councillor'}.</p>
              </div>
              <textarea
                value={responseText}
                onChange={e => setResponseText(e.target.value)}
                rows={8}
                placeholder={`Paste or type the councillor's response here...\n\n(This will be featured at the top of ${responseModal.ward}'s feed for 48 hours)`}
                className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-4 py-3 font-mono focus:outline-none focus:border-red-600 resize-none"
                data-testid="councillor-response-textarea"
              />
              <div className="flex gap-3">
                <button onClick={sendResponse} disabled={!responseText.trim()}
                  className="bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-xs px-6 py-2 border border-red-600 font-mono uppercase tracking-wider">
                  POST + PIN TO WARD FEED
                </button>
                <button onClick={() => setResponseModal(null)} className="text-gray-500 hover:text-white text-xs border border-[#2a2a2a] px-4 py-2">CANCEL</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WardPulseAdmin;
